import React from 'react'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { MultipleChoiceElement } from 'modules/forms/lib/elements/MultipleChoiceElement'
import { SingleChoiceElement } from 'modules/forms/lib/elements/SingleChoiceElement'

import { OptionElementPreview } from './OptionElementPreview'

export const patchElementsForPreview = (elements: Element[]): void => {
  elements.forEach((element) => {
    if (element instanceof MultipleChoiceElement || element instanceof SingleChoiceElement) {
      element.getPreviewComponent = (disabled?: boolean) => {
        return React.createElement(OptionElementPreview, {
          element,
          isSingleChoice: element instanceof SingleChoiceElement,
          disabled,
        })
      }
    }
  })
}

export const validateQuizAnswers = (form: Form | undefined): boolean => {
  if (!form) return false

  let allCorrect = true

  form.elements.forEach((element) => {
    const userAnswer = element.answer

    if (!element.hasCorrectAnswer) {
      if (userAnswer) {
        userAnswer.isCorrect = true
      }
      return
    }

    if (!userAnswer || !userAnswer.options || userAnswer.options.length === 0) {
      allCorrect = false
      return
    }

    const correctOptionIds = element.options.filter((opt) => opt.isCorrect).map((opt) => opt.id)
    const selectedOptionIds = userAnswer.options
    const isElementCorrect =
      selectedOptionIds.length === correctOptionIds.length &&
      selectedOptionIds.every((selectedId) => correctOptionIds.includes(selectedId))

    userAnswer.isCorrect = isElementCorrect

    if (!isElementCorrect) {
      allCorrect = false
    }
  })

  return allCorrect
}

interface HandleSubmitPreviewModeParams {
  form: Form | undefined
  hasToRetry: boolean
  submissionResult: 'passed' | 'failed' | null
  isCompleted: boolean
  setNumberOfAnswers: (n: number) => void
  setSubmissionResult: (result: 'passed' | 'failed' | null) => void
  setIsRetrying: (retrying: boolean) => void
  updateNextButton: () => void
  scrollToTop: () => void
}

export const handleSubmitPreviewMode = async ({
  form,
  hasToRetry,
  submissionResult,
  isCompleted,
  setNumberOfAnswers,
  setSubmissionResult,
  setIsRetrying,
  updateNextButton,
  scrollToTop,
}: HandleSubmitPreviewModeParams): Promise<boolean> => {
  if (!form) return true

  if (hasToRetry) {
    form.resetFormAnswers()
    setNumberOfAnswers(0)
    setSubmissionResult(null)
    setIsRetrying(true)
    updateNextButton()
    scrollToTop()
    return false
  }

  if (submissionResult !== null || isCompleted) {
    return true
  }

  const allCorrect = validateQuizAnswers(form)
  const result = allCorrect ? 'passed' : 'failed'
  setSubmissionResult(result)
  setIsRetrying(false)
  setNumberOfAnswers(form.numberOfAnswers())

  patchElementsForPreview(form.elements)

  updateNextButton()
  scrollToTop()

  return false
}
